import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  COMPLEX_TO_SIMPLE_QUERY_PROMPT,
  QUERY_EXPANSION,
  MULTI_QUERY_PROMPT,
  HYDE_PROMPT,
} from "../../../../../../utils/defaultPrompts";
import { Clear } from "@mui/icons-material";
import { dialogStyles } from "../../../../../../Constants/dialogStyles";
import { inputStyles } from "../../../../../../Constants/inputStyles";
import { createLLMTypography } from "../../../../../../Constants/theme";
import {
  primaryButtonStyles,
  tertiaryButtonStyles,
} from "../../../../../../Constants/buttonStyles";

const EditPromptsDialog = ({
  open,
  onClose,
  prompts,
  updatePrompt,
  setBotData,
  nodeId,
  organization,
}) => {
  const [selectedPromptType, setSelectedPromptType] = useState("");
  const [editablePrompt, setEditablePrompt] = useState("");
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [promptOptionsState, setPromptOptionsState] = useState({
    complex_to_simple: {
      label: "Deconstruction",
      value: prompts.complex_to_simple,
    },
    query_expansion: {
      label: "Expansion",
      value: prompts.query_expansion,
    },
    multi_query: {
      label: "Multi-phrasing",
      value: prompts.multi_query,
    },
    hyde: {
      label: "HyDE",
      value: prompts.hyde,
    },
    iterative_deconstruction: {
      label: "Iterative Deconstruction",
      value: prompts.iterative_deconstruction,
    },
  });

  const isStarterSubscription =
    organization?.organization?.subscription === "starter";

  const promptDefaults = {
    complex_to_simple: COMPLEX_TO_SIMPLE_QUERY_PROMPT,
    query_expansion: QUERY_EXPANSION,
    multi_query: MULTI_QUERY_PROMPT,
    hyde: HYDE_PROMPT,
  };

  useEffect(() => {
    if (open) {
      // Get all prompt keys
      const promptKeys = Object.keys(promptOptionsState);

      // Filter prompts based on subscription level
      const allowedPrompts = isStarterSubscription
        ? promptKeys.filter(
            (key) =>
              ![
                "complex_to_simple",
                "query_expansion",
                "iterative_deconstruction",
              ].includes(key)
          )
        : promptKeys;

      // Select the first allowed prompt or an empty string if none are allowed
      const defaultPromptType = allowedPrompts[0] || "";

      setSelectedPromptType(defaultPromptType);
      setEditablePrompt(promptOptionsState[defaultPromptType]?.value || "");
      setOriginalPrompt(promptOptionsState[defaultPromptType]?.value || "");
    }
  }, [open]);

  const handlePromptTypeChange = (event) => {
    const selectedType = event.target.value;
    setSelectedPromptType(selectedType);

    // Load the corresponding value from the state
    const selectedValue = promptOptionsState[selectedType]?.value || "";
    setEditablePrompt(selectedValue);
    setOriginalPrompt(selectedValue);
  };

  const handleEditPrompt = (event) => {
    const updatedValue = event.target.value;
    setEditablePrompt(updatedValue);

    // Update the specific prompt in the state
    setPromptOptionsState((prevState) => ({
      ...prevState,
      [selectedPromptType]: {
        ...prevState[selectedPromptType],
        value: updatedValue,
      },
    }));
  };

  const handleReset = () => {
    if (selectedPromptType) {
      setEditablePrompt(promptDefaults[selectedPromptType]); // Set to default prompt value
    }
  };

  const validatePromptString = (prompt, type) => {
    // Ensure the prompt has triple quotes at the start and end
    let validatedPrompt = prompt;

    // Add "Query: {query}" if {query} is missing in general prompts
    if (!validatedPrompt.includes("{query}")) {
      validatedPrompt += `\nQuery: {query}`;
    }

    // For iterative_deconstruction, add "Context: {context}" if {context} is missing
    if (
      type === "iterative_deconstruction" &&
      !validatedPrompt.includes("{context}")
    ) {
      validatedPrompt += `\nContext: {context}`;
    }

    if (!validatedPrompt.startsWith(`"""`)) {
      validatedPrompt = `"""\n${validatedPrompt}`;
    }
    if (!validatedPrompt.endsWith(`"""`)) {
      validatedPrompt = `${validatedPrompt}\n"""`;
    }

    return validatedPrompt;
  };

  const handleSave = () => {
    // Validate and update all prompts
    const convertedPromptOptions = Object.fromEntries(
      Object.entries(promptOptionsState).map(([key, { value }]) => [
        key,
        validatePromptString(value, key),
      ])
    );
    // Update `promptOptionsState` with validated prompts
    setPromptOptionsState((prevState) => {
      const updatedState = Object.entries(prevState).reduce(
        (acc, [key, obj]) => {
          acc[key] = {
            ...obj,
            value: convertedPromptOptions[key], // Use the validated value
          };
          return acc;
        },
        {}
      );
      return updatedState;
    });

    // Update the bot data
    setBotData((prevData) => ({
      ...prevData,
      query_preprocessing_prompts: { [`${nodeId}`]: convertedPromptOptions },
    }));

    // Save all prompts to localStorage
    try {
      localStorage.setItem(
        `queryTransform_prompts_${nodeId}`,
        JSON.stringify(convertedPromptOptions)
      );
    } catch (error) {
      console.error("Error saving prompts to localStorage:", error);
    }

    onClose(); // Close the dialog
  };

  const isSaveDisabled = editablePrompt === originalPrompt;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth={"md"}
      PaperProps={{ sx: dialogStyles.dialogRoot }}
    >
      <DialogTitle sx={dialogStyles.dialogTitle}>
        <Typography sx={dialogStyles.dialogTitleText}>
          Edit Query Transformations Prompts
        </Typography>
        <Clear onClick={onClose} sx={dialogStyles.dialogCloseIcon} />
      </DialogTitle>
      <DialogContent sx={{ ...dialogStyles.dialogContent, mt: 2 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography sx={inputStyles.label}>Select Prompt Type</Typography>
          <Select
            value={selectedPromptType}
            onChange={handlePromptTypeChange}
            sx={inputStyles.select}
          >
            {Object.keys(promptOptionsState).map((key) => {
              const isDisabled =
                isStarterSubscription &&
                [
                  "complex_to_simple",
                  "query_expansion",
                  "iterative_deconstruction",
                ].includes(key);
              return (
                <MenuItem
                  key={key}
                  value={key}
                  disabled={isDisabled}
                  sx={createLLMTypography.fontSizes.sectionText2}
                >
                  {promptOptionsState[key].label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {selectedPromptType && (
          <Tooltip
            title={
              isStarterSubscription &&
              [
                "complex_to_simple",
                "query_expansion",
                "iterative_deconstruction",
              ].includes(selectedPromptType)
                ? "You need to upgrade your subscription to edit this prompt."
                : ""
            }
          >
            <TextField
              fullWidth
              multiline
              rows={10}
              value={editablePrompt}
              onChange={handleEditPrompt}
              placeholder="Edit the selected prompt here"
              sx={inputStyles.textField}
              disabled={
                isStarterSubscription &&
                [
                  "complex_to_simple",
                  "query_expansion",
                  "iterative_deconstruction",
                ].includes(selectedPromptType)
              }
            />
          </Tooltip>
        )}
      </DialogContent>
      <DialogActions sx={dialogStyles.dialogActions}>
        <Tooltip title="Reset Current Prompt to its default value">
          <Button
            sx={tertiaryButtonStyles}
            onClick={handleReset}
            disabled={
              isStarterSubscription &&
              [
                "complex_to_simple",
                "query_expansion",
                "iterative_deconstruction",
              ].includes(selectedPromptType)
            }
          >
            Reset
          </Button>
        </Tooltip>
        {/* <Tooltip title={`Regenerate using your problem statement`}>
          {" "}
          <Button sx={btnStyles} onClick={handleRegenerate}>
            Regenerate using problem statement
          </Button>
        </Tooltip> */}

        <Tooltip title="Save All Changes">
          <Button
            sx={primaryButtonStyles}
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            Save All
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default EditPromptsDialog;
